import { PlusIcon } from '@heroicons/react/24/outline';
import React, { type FC, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { useCreativeListQuery, useDeleteCreative, useDuplicateCreative } from '@/api/queries/creative';
import type { Creative, CreativeListResponse, CreativeQuery } from '@/api/types/creative';
import { AllTemplatesIcon } from '@/common/all-templates-icon';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { CircularLoadingAnimation } from '@/common/circular-loading-animation';
import { ConfirmationModal, ConfirmationModalType } from '@/common/confimation-modal';
import { EmptyIcon } from '@/common/empty-icon';
import { FloatingPanel, FloatingPanelItem } from '@/common/floating-panel';

import { CreativeTable } from './creative-table';

enum CreativeListMode {
    ALL = 'ALL',
    DRAFT = 'DRAFT',
    EXPORTED = 'EXPORTED',
}

export const CreativesList: FC = () => {
    const [creativesList, setCreativesList] = useState<Creative[]>([]);
    const [creativeListModes, setCreativeListModes] = useState<CreativeListMode>(CreativeListMode.ALL);
    const [draftCount, setDraftCount] = useState<number>(0);
    const [exportedCount, setExportedCount] = useState<number>(0);
    const [allCount, setAllCount] = useState<number>(0);
    const [isCreativeDeleteConfirmationOpen, setIsCreativeDeleteConfirmationOpen] = useState(false);
    const [creativeForDeletion, setCreativeForDeletion] = useState<number | null>(null);
    const [query, setQuery] = useState<CreativeQuery>({});
    const [isFetching, setIsFetching] = useState(true);

    const { mutate: duplicateCreative } = useDuplicateCreative();
    const { mutate: deleteCreative } = useDeleteCreative();
    const { refetch: _ } = useCreativeListQuery(query, {
        onSuccess: (data: CreativeListResponse) => {
            setCreativesList(data.results);
            setDraftCount(data.draftCount);
            setExportedCount(data.exportCount);
            setAllCount(data.totalCount);
            setIsFetching(false);
        },
    });

    const getEmptyStateMessage = () => {
        if (creativeListModes === CreativeListMode.ALL) {
            return 'Start with your first Project.';
        }
        if (creativeListModes === CreativeListMode.DRAFT) {
            return 'No Draft Creatives.';
        }
        if (creativeListModes === CreativeListMode.EXPORTED) {
            return 'No Exported Creatives.';
        }
        return 'No Creatives Found.';
    };

    const getHeaderTitle = () => {
        if (creativeListModes === CreativeListMode.ALL) {
            return 'All Creatives';
        }
        if (creativeListModes === CreativeListMode.DRAFT) {
            return 'Drafts';
        }
        if (creativeListModes === CreativeListMode.EXPORTED) {
            return 'Exported Creatives';
        }
        return '';
    };

    useEffect(() => {
        if (creativeForDeletion !== null) {
            setIsCreativeDeleteConfirmationOpen(true);
        }
    }, [creativeForDeletion]);

    useEffect(() => {
        if (!isCreativeDeleteConfirmationOpen) {
            setCreativeForDeletion(null);
        }
    }, [isCreativeDeleteConfirmationOpen]);

    useEffect(() => {
        if (creativeListModes === CreativeListMode.ALL) {
            setQuery({});
            return;
        }
        setQuery({ draft: creativeListModes === CreativeListMode.DRAFT });
    }, [creativeListModes]);

    const navigate = useNavigate();

    return (
        <div className="flex min-h-0 flex-row h-full">
            {isFetching ? (
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <CircularLoadingAnimation className="w-16 h-16" />
                    <p className="text-gray-60 text-2xl">Loading creatives ...</p>
                </div>
            ) : (
                <>
                    {allCount > 0 && (
                        <FloatingPanel
                            className="px-4 h-full w-[250px] flex-shrink-0 overflow-y-auto"
                            omitSeperator={true}
                        >
                            <FloatingPanelItem className="px-4 my-2">
                                <Button
                                    className="w-full"
                                    title="New Creative"
                                    buttonType={ButtonType.PRIMARY}
                                    buttonSize={ButtonSize.LARGE}
                                    leading={<PlusIcon className="h-5 w-5 text-white" />}
                                    onClick={() => navigate('/creatives/create')}
                                />
                                <hr className="bg-gray-100 border-gray-100 w-full mt-4" />
                            </FloatingPanelItem>
                            <FloatingPanelItem className="flex flex-col mt-2 px-4">
                                <div className="flex flex-col">
                                    <div className="flex flex-col gap-2">
                                        <div
                                            onClick={() => {
                                                setCreativeListModes(CreativeListMode.ALL);
                                            }}
                                            onKeyUp={() => {
                                                setCreativeListModes(CreativeListMode.ALL);
                                            }}
                                            className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 ${creativeListModes === CreativeListMode.ALL ? 'bg-gray-100' : ''}`}
                                        >
                                            <div className="flex flex-row gap-2 items-center">
                                                <AllTemplatesIcon
                                                    className={`w-4 h-4 ${creativeListModes === CreativeListMode.ALL ? 'stroke-white' : 'stroke-gray-60'}`}
                                                />{' '}
                                                <span
                                                    className={`${creativeListModes === CreativeListMode.ALL ? 'text-white' : 'text-gray-60'} text-sm font-normal`}
                                                >
                                                    All Creative
                                                </span>
                                            </div>
                                            <p
                                                className={`flex h-6 w-6 bg-gray-100 rounded-md ${creativeListModes === CreativeListMode.ALL ? 'text-white' : 'text-gray-60'} text-xs text-center justify-center items-center`}
                                            >
                                                {allCount ?? 0}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-1.5 ml-6">
                                        <div
                                            onClick={() => {
                                                setCreativeListModes(CreativeListMode.DRAFT);
                                            }}
                                            onKeyUp={() => {
                                                setCreativeListModes(CreativeListMode.DRAFT);
                                            }}
                                            className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 ${creativeListModes === CreativeListMode.DRAFT ? 'bg-gray-100' : ''}`}
                                        >
                                            <span
                                                className={`${creativeListModes === CreativeListMode.DRAFT ? 'text-white' : 'text-gray-60'} text-xs font-normal`}
                                            >
                                                Drafts
                                            </span>
                                            <p
                                                className={`flex h-6 w-6 bg-gray-100 rounded-md ${creativeListModes === CreativeListMode.DRAFT ? 'text-white' : 'text-gray-60'} text-xs text-center justify-center items-center`}
                                            >
                                                {draftCount ?? 0}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-2 mt-1.5 ml-6">
                                        <div
                                            onClick={() => {
                                                setCreativeListModes(CreativeListMode.EXPORTED);
                                            }}
                                            onKeyUp={() => {
                                                setCreativeListModes(CreativeListMode.EXPORTED);
                                            }}
                                            className={`flex flex-row justify-between items-center rounded-md px-2.5 py-1.5 hover:cursor-pointer hover:bg-gray-150 ${creativeListModes === CreativeListMode.EXPORTED ? 'bg-gray-100' : ''}`}
                                        >
                                            <span
                                                className={`${creativeListModes === CreativeListMode.EXPORTED ? 'text-white' : 'text-gray-60'} text-xs font-normal`}
                                            >
                                                Exported Creative
                                            </span>
                                            <p
                                                className={`flex h-6 w-6 bg-gray-100 rounded-md ${creativeListModes === CreativeListMode.EXPORTED ? 'text-white' : 'text-gray-60'} text-xs text-center justify-center items-center`}
                                            >
                                                {exportedCount ?? 0}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </FloatingPanelItem>
                        </FloatingPanel>
                    )}

                    <div className="mt-2 p-5 flex flex-col flex-grow overflow-hidden">
                        {allCount > 0 && (
                            <p className="text-white text-2xl font-normal items-start">{getHeaderTitle()}</p>
                        )}
                        {creativesList.length === 0 && (
                            <div className="flex flex-col items-center justify-center flex-grow">
                                {creativeListModes === CreativeListMode.ALL && <EmptyIcon />}
                                <p
                                    className={`${creativeListModes === CreativeListMode.ALL ? 'text-white text-3xl' : 'text-gray-40 text-xl'} font-normal mt-10`}
                                >
                                    {getEmptyStateMessage()}
                                </p>
                                {creativeListModes === CreativeListMode.ALL && (
                                    <Button
                                        className="mt-4"
                                        title="New Creative"
                                        buttonType={ButtonType.PRIMARY}
                                        buttonSize={ButtonSize.LARGE}
                                        leading={<PlusIcon className="h-5 w-5 text-white" />}
                                        onClick={() => navigate('/creatives/create')}
                                    />
                                )}
                            </div>
                        )}
                        {creativesList.length > 0 && (
                            <CreativeTable
                                className="mt-10"
                                creatives={creativesList}
                                onDelete={setCreativeForDeletion}
                                onDuplicate={(id) => {
                                    duplicateCreative(id);
                                }}
                            />
                        )}
                    </div>
                </>
            )}
            <ConfirmationModal
                isOpen={isCreativeDeleteConfirmationOpen}
                setIsOpen={setIsCreativeDeleteConfirmationOpen}
                type={ConfirmationModalType.WARN}
                confirmText="Delete"
                cancelText="Cancel"
                message="Delete Creative"
                subMessage="Are you sure you want to delete this creative?"
                onConfirm={() => {
                    deleteCreative(creativeForDeletion as number);
                }}
            />
        </div>
    );
};
